/**
 * ══════════════════════════════════════════════════════════════════════════
 * KLİP ALAN ADLARI — TEK GERÇEK (saf: import yok, DOM yok, I/O yok)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ARIZA SINIFI (2026-08-25, iki bağımsız denetimde 9 vaka):
 * **YAZAN TARAF `v3_*` ADINI KULLANIYOR, OKUYAN TARAF ESKİ ADI.**
 *
 * V3 hattının GERÇEKTEN yazdığı adlar (ölçüldü):
 *   · `cloud-run-transcoder/lib/firestore.js:85,141` → **`v3_hlsManifestUrl`**
 *   · `myclipsv3/compose/pipeline/firestoreWrite.ts:444` → **`v3_posterUrl`**
 *   · R2 video → **`v3_r2Url`**
 *   · puan denormu → **`v3_ratingCount` / `v3_ratingAvg`**
 * Transcoder'da `manifestUrl` adı **0 kez** geçiyor.
 *
 * Buna karşılık okuyan yüzeyler eski adlarda kalmıştı:
 *   · `ClipInFeed` → `clip.manifestUrl`, `clip.posterUrl || clip.thumbUrl`
 *     → ana akışta HLS/ABR HİÇ kullanılmıyor (ham video iniyor) ve kapak YOK
 *   · `ClipDetailModalDesktop` → yalnız eski adlar → `hasAnySource === false`
 *     → **sağlam her V3 klip masaüstünde "Video bulunamadı"** (gözle görüldü)
 *   · `savesClient` → klip kapağı yerine VİDEO url'i (`<img src="....m3u8">`)
 *
 * 🧭 `ClipDetailModalMobile` 2026-08-16'da onarılmıştı ve kendi yorumunda
 * *"normalize adlar ÖNCE okunur"* yazıyor — ama onarım **tek ikize**
 * uygulanmış. Bu modül o onarımı TEK YERE indirir: bir daha bir ikiz
 * güncellenip diğeri unutulamaz.
 *
 * TASARIM: eski adlar SİLİNMEDİ, sıraya alındı. Sebep ölçülü — legacy V2
 * klipleri (`v3_uploadedVia ≠ "v3"`) hâlâ eski adları taşıyor ve
 * etiketlenenler ızgarası onları listeliyor. Sözleşme GENİŞLEDİ, daralmadı.
 * ══════════════════════════════════════════════════════════════════════════
 */

/** Okunabilir en küçük klip şekli. Alanların hepsi isteğe bağlı. */
export type KlipAlanKaynagi = {
  // — HLS / manifest —
  v3_hlsManifestUrl?: unknown;
  hlsManifestUrl?: unknown;
  manifestUrl?: unknown;
  // — video (progressive / R2) —
  src?: unknown;
  v3_r2Url?: unknown;
  mp4Url?: unknown;
  mediaUrl?: unknown;
  videoUrl?: unknown;
  url?: unknown;
  sourceUrl?: unknown;
  // — kapak —
  v3_posterUrl?: unknown;
  posterUrl?: unknown;
  thumbUrl?: unknown;
  previewUrl?: unknown;
  // — 5★ —
  v3_ratingCount?: unknown;
  v3_ratingAvg?: unknown;
  // — yorum sayısı ("Tüm N yorumu gör") —
  v3_commentCount?: unknown;
  yorumlar?: unknown;
  // — süre (▶ rozeti) —
  // İKİ AD: `v3_durationMs` V3 hattının kanonik adı, `durationMs` eski/keşif
  // adaptörlerinin ürettiği ad (`myclipsv3/services/candidateRetrieval.ts:222`
  // ikisini de okuyor). Öncelik sırası `rankPolicyV3.ts`teki yedek zinciriyle
  // BİREBİR aynı tutuldu — aynı alanı iki dosyada iki farklı öncelikle okumak,
  // aynı klibin iki yüzeyde iki farklı süre göstermesi demektir.
  v3_durationMs?: unknown;
  durationMs?: unknown;
};

/** Boş olmayan bir dize mi? (0, false, "" ve boşluk hepsi elenir.) */
function metin(v: unknown): string {
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

/**
 * HLS manifesti. YOKSA boş dize — çağıran o zaman progressive yedeğe düşer.
 * `v3_*` ÖNCE: V3 klibinde iki ad birden bulunursa kanonik olan kazanmalı.
 */
export function klipManifestUrl(k?: KlipAlanKaynagi | null): string {
  return metin(k?.v3_hlsManifestUrl) || metin(k?.hlsManifestUrl) || metin(k?.manifestUrl);
}

/**
 * Oynatılabilir video (manifest yoksa yedek).
 * `src` en önde: `clipsRepo.clipFromSnap` normalize edilmiş çıktısını oraya
 * koyuyor ve o çıktı R2 rewrite'ından GEÇMİŞ hâldir.
 */
export function klipVideoUrl(k?: KlipAlanKaynagi | null): string {
  return (
    metin(k?.src) ||
    metin(k?.v3_r2Url) ||
    metin(k?.mp4Url) ||
    metin(k?.mediaUrl) ||
    metin(k?.videoUrl) ||
    metin(k?.url) ||
    metin(k?.sourceUrl)
  );
}

/**
 * KAPAK (poster). Video url'i ASLA kapak değildir.
 *
 * 🔴 Bu ayrım bir kusurdan doğdu: `savesClient` klip için `v3_r2Url`
 * (yani `.m3u8`/video) döndürüyor, `SavedGrid` onu `<img src>` içine
 * koyuyordu → kaydedilenlerde klip karosu DAİMA boş. Kardeş ızgaralar
 * (`UserPosts`, `TaggedGridLite`) doğru alanı okuyordu; kaydedilenler
 * poster zincirini hiç kopyalamamıştı.
 */
export function klipPosterUrl(k?: KlipAlanKaynagi | null): string {
  return metin(k?.v3_posterUrl) || metin(k?.posterUrl) || metin(k?.thumbUrl) || metin(k?.previewUrl);
}

/** Oynatılabilir bir kaynak var mı? (manifest VEYA video) */
export function klipOynatilabilir(k?: KlipAlanKaynagi | null): boolean {
  return !!(klipManifestUrl(k) || klipVideoUrl(k));
}

/** 5★ oy sayısı; yoksa 0. (Emekli `begenenler` ASLA okunmaz.) */
export function klipPuanSayisi(k?: KlipAlanKaynagi | null): number {
  const n = Number(k?.v3_ratingCount);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Klip yorum sayısı. Bilinmiyorsa **0**.
 *
 * ⚠️ ZİNCİR KOPYA DEĞİL, EŞLEŞTİRİLDİ: `feed/clipsRepo.ts:415` ve
 * `stats/useClipStats.ts:102` ikisi de `v3_commentCount ?? yorumlar.length`
 * yazıyor — yani GÖSTERİM yüzeylerinin sözleşmesi budur ve `??` bilinçlidir
 * (denorm alan gerçekten `0` ise, eski `yorumlar` dizisine DÜŞÜLMEZ; `||`
 * olsaydı 0 yanlışlıkla yedeğe düşer ve silinmiş yorumlar geri "canlanırdı").
 * `ranking/rankPolicyV3.ts` `||` kullanıyor ama o bir SIRALAMA girdisidir,
 * gösterim değil — iki ayrı iş, iki ayrı karar. Aynı klibin akıştaki sayısı
 * MyClips'teki sayısıyla aynı çıksın diye gösterim tarafı seçildi.
 */
export function klipYorumSayisi(k?: KlipAlanKaynagi | null): number {
  const denorm = k?.v3_commentCount;
  if (denorm != null) {
    const n = Number(denorm);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return Array.isArray(k?.yorumlar) ? (k as { yorumlar: unknown[] }).yorumlar.length : 0;
}

/**
 * Klip süresi (ms). Bilinmiyorsa **0** — "yok" ile "sıfır uzunluk" burada
 * AYNI kovadadır ve bilinçlidir: sıfır uzunlukta bir klip yayınlanamaz
 * (`kayitSuresi.ts` alt sınır koyuyor), yani 0 ancak "alan yok/bozuk"
 * demektir. Çağıran tek bir `> 0` kontrolüyle rozeti susturur.
 *
 * ⚠️ `Number("")` = 0 ve `Number(null)` = 0 → boş dize/null da 0'a düşer,
 * bu istenen davranış. `Number.isFinite` NaN ve Infinity'yi eler.
 */
export function klipSureMs(k?: KlipAlanKaynagi | null): number {
  const adaylar = [k?.v3_durationMs, k?.durationMs];
  for (const ham of adaylar) {
    const n = Number(ham);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/**
 * Süre rozetinin metni: `0:14` · `1:03` · `12:07`. Bilinmiyorsa **boş dize**
 * (çağıran o zaman yalnız ▶ basar — IG'nin akış Reel rozeti de sürenin
 * bilinmediği durumda yalnız ▶'dir; bkz. `docs/denetim/IG_REFERANS_AKIS.md` §4).
 *
 * `Math.round` DEĞİL `Math.floor`: 13,6 sn'lik bir klip "0:14" yazıp
 * oynatıcıda 13'te bitmemeli — geçen süre sayacı gibi aşağı yuvarlanır
 * (video oynatıcılarının yerleşik davranışı).
 *
 * Saat yok: `kayitSuresi.ts` üst sınırı dakikalar mertebesinde; 60+ dakikalık
 * bir klip bu üründe üretilemez. Yine de 60 dk aşılırsa dakika alanı büyür
 * (`75:00`) — sessizce YANLIŞ bir "15:00" yazmaktansa görünür biçimde taşar.
 */
export function klipSureMetni(k?: KlipAlanKaynagi | null): string {
  const ms = klipSureMs(k);
  if (ms <= 0) return "";
  const toplamSn = Math.floor(ms / 1000);
  const dk = Math.floor(toplamSn / 60);
  const sn = toplamSn % 60;
  return `${dk}:${String(sn).padStart(2, "0")}`;
}
